import { Injectable } from '@angular/core';
import { ICartItem, IExtra, IExtraResponseObj, IExtraRoomPrice } from '../interfaces';
import { HttpClient } from '@angular/common/http';
import { SettingsService } from './settings.service';
import { v4 as uuidv4 } from 'uuid';
import { ShopcartService } from './shopcart.service';
import { catchError, forkJoin, map, Observable, of, pipe } from 'rxjs';
import { PIMService } from './pim.service';
import { IShopCart } from '../interfaces';
import { DebugService } from './debug.service';
import {unionBy} from 'lodash';
import { LanguageService } from './language.service';
import dayjs from 'dayjs';

@Injectable({
  providedIn: 'root'
})
export class ExtrasService {

  constructor(
    private http: HttpClient, 
    private settings: SettingsService, 
    private shopCartService: ShopcartService, 
    private PIM: PIMService,
    private debug: DebugService,
    private languageService: LanguageService
  ) { }

  getExtras():Observable<any> {

    let endpoint = this.settings.getEndpoint();
    let url = `${endpoint}/Availability/extrarates`;
    let shopCart = this.shopCartService.getCart();

    if (!shopCart) {
      return of(false);
    }

    let searches:Observable<Object>[] = [];
    let conversationId = uuidv4();
    shopCart.getCartItemsByType('roomrate').forEach((cartItem:ICartItem, index:number) => {

      let clonedCartItem = JSON.parse(JSON.stringify(cartItem));
      delete clonedCartItem.item.rate.pim;
      delete clonedCartItem.item.room.pim;

      // the backend checks the room rate signature against these values, so they are sent like in the room rate
      // search and the reservation: dates without timezone and the ages of the room's children
      let searchMask = {
        "Arrival": dayjs(clonedCartItem.searchParams.arrival).format('YYYY-MM-DDTHH:mm:ss'),
        "Departure": dayjs(clonedCartItem.searchParams.departure).format('YYYY-MM-DDTHH:mm:ss'),
        "BookingCode": clonedCartItem.searchParams?.bookingCode,
        "Adults": clonedCartItem.searchParams.roomData[index].adults,
        "Children": clonedCartItem.searchParams.roomData[index].children,
        "ChildAges": clonedCartItem.searchParams.roomData[index].childAges,
        "Rooms": 1
      };
  
      let body = {
        "hotelCode": clonedCartItem.searchParams.hotelCode,
        "searchMask": searchMask,
        "roomRate": clonedCartItem.item,
      }

      searches.push(this.http.post(url, body, {
        responseType: 'json',
        headers: {
          'X-Conversation-ID': uuidv4()
        }
      }))
    });
  
    return forkJoin(searches).pipe(map((responses:any[]) => {
      
      let modifiedResponses:any = [];
      let extrasPerRoom:any = [];
      // each room's extras are signed with that room's rate, so the price and signature are kept per room
      let pricesPerRoom: { [rateId: string]: { [roomIndex: number]: IExtraRoomPrice } } = {};
      let allHidden = true;
    
      responses.forEach((res:any, index:number) => {
        let data:IExtraResponseObj[] = res.data; 
        
        if (data) {
          data.forEach((extraResObj:IExtraResponseObj) => {
            let extra:IExtra = extraResObj; 
            extra['pim'] = this.PIM.getExtrasData(shopCart.items[0].searchParams.hotelCode, extraResObj.rateId.toString());
            if (extra.pim?.rules) {
              extra['appliedRules'] = this.applyRules(extra);
              if (!extra.appliedRules?.hiddenInAllRoomTypes) {
                allHidden = false
              }
            }
            
            else {
              allHidden = false;
            }

            pricesPerRoom[extraResObj.rateId] ??= {};
            pricesPerRoom[extraResObj.rateId][index] = {
              unitPriceForStayWithTaxes: extraResObj.unitPriceForStayWithTaxes,
              signature: extraResObj.signature
            };

            let foundExtraPerRoom = extrasPerRoom.find((item:any) => item.rateId == extraResObj.rateId);
            if (foundExtraPerRoom) {
              foundExtraPerRoom.roomIndex.push(index);
            }
            else {
              extrasPerRoom.push({
                rateId: extraResObj.rateId,
                roomIndex: [index]
              });
            }
            
          });
        }
        
        
        modifiedResponses.push(data);
      });

      if (allHidden) {
        return false;
      }

      let returnObject:any = modifiedResponses[0];
      if (modifiedResponses.length > 1) {
        let otherItems = JSON.parse(JSON.stringify(modifiedResponses));
        otherItems.shift();
        returnObject = unionBy(returnObject,  ...otherItems, 'rateId');
      }

      //add info of what rooms are bookable for extra
      returnObject.forEach((extra:any) => {
        let foundExtraPerRoom = extrasPerRoom.find((item:any) => item.rateId == extra.rateId);
        extra.availableRoomIndexes = foundExtraPerRoom.roomIndex
        extra.roomPrices = pricesPerRoom[extra.rateId];
      });

      this.debug.log('Extras after rules and combined per room', returnObject);
      return returnObject;
        
    }),
    catchError((e:any, caught: Observable<any>):any => {
      console.log('error', e);
    }))
     
  }

  getExternalExtras():Observable<any> {
    let shopCart = this.shopCartService.getCart();

    if (!shopCart) {
      return of(false);
    }

    let endpoint = this.settings.getEndpoint();
    let start = shopCart.getFirstSearchParams().arrival;
    let end = shopCart.getFirstSearchParams().departure;
    let hotelCode = shopCart.getFirstSearchParams().hotelCode;
    let language = this.languageService.getCurrentLanguage().toUpperCase();
    start = typeof start == 'object' ? start.toISOString().split('T')[0] : start.split('T')[0];
    end = typeof end == 'object' ? end.toISOString().split('T')[0] : end.split('T')[0];

    let url = new URL(`${endpoint}/tickets`);
    url.searchParams.append('eventDateFrom', start);
    url.searchParams.append('eventDateTo', end);
    url.searchParams.append('HotelId', hotelCode);
    url.searchParams.append('language', language);

    let finalUrl = url.toString();


    return this.http.get(finalUrl, {
      responseType: 'json',
      headers: {
        'X-Conversation-ID': uuidv4()
      }
    })
  
  }


  applyRules(extra:IExtra) {
    let cart = this.shopCartService.getCart();
 
    if (extra.pim?.rules && cart) {

      let rules = extra.pim?.rules;
      let checkedRules = this.checkCartRulesPerRoom(cart, rules);
      this.debug.log(`rules for ${extra.rateId}`,  checkedRules);
      return checkedRules;
    }
    return undefined;
    
  }

  checkCartRulesPerRoom(cart: any, rules: any) {
    let results:any = [];
    
    cart.items.forEach((item:ICartItem, index:number) => {
      let failedRules = [];
      let roomData = item.searchParams.roomData[index];

      if (rules.showIfChildCountLessThan !== undefined && roomData.children >= rules.showIfChildCountLessThan) {
        failedRules.push(`showIfChildCountLessThan: ruleValue: ${rules.showIfChildCountLessThan}, childCountInCartItem: ${roomData.children}`);
      }
      
      if (rules.showIfChildCountMoreThan !== undefined && roomData.children <= rules.showIfChildCountMoreThan) {
        failedRules.push(`showIfChildCountMoreThan: ruleValue: ${rules.showIfChildCountMoreThan}, childCountInCartItem: ${roomData.children}`);
      }
      
      if (rules.showIfAdultCountLessThan !== undefined && roomData.adults >= rules.showIfAdultCountLessThan) {
        failedRules.push(`showIfAdultCountLessThan: ruleValue: ${rules.showIfAdultCountLessThan}, adultCountInCartItem: ${roomData.adults}`);
      }
      
      if (rules.showIfAdultCountMoreThan !== undefined && roomData.adults <= rules.showIfAdultCountMoreThan) {
        failedRules.push(`showIfAdultCountMoreThan: ruleValue: ${rules.showIfAdultCountMoreThan}, adultCountInCartItem: ${roomData.adults}`);
      }

      if (rules.showIfChildAgeLessThan !== undefined && roomData.childAges.every((age) => age !== null && age >= rules.showIfChildAgeLessThan) ) {
        failedRules.push(`showIfChildAgeLessThan: ruleValue: ${rules.showIfChildAgeLessThan}, childAges: ${roomData.childAges}`);
      }

      if (rules.showIfChildAgeMoreThan !== undefined && roomData.childAges.every((age) => age !== null && age <= rules.showIfChildAgeMoreThan) ) {
        failedRules.push(`showIfChildAgeMoreThan: ruleValue: ${rules.showIfChildAgeMoreThan}, childAges: ${roomData.childAges}`);
      }
      
      if (rules.showForRoomCodes && !rules.showForRoomCodes.includes(item.item.room.roomCode)) {
        failedRules.push(`showForRoomCodes: ruleValue: ${rules.showForRoomCodes}, roomCodeInCartItem: ${item.item.room.roomCode}`);
      }
      
      if (rules.hideForRoomCodes && rules.hideForRoomCodes.includes(item.item.room.roomCode)) {
        failedRules.push(`hideForRoomCodes: ruleValue: ${rules.hideForRoomCodes}, roomCodeInCartItem: ${item.item.room.roomCode}`);
      }
      
      if (rules.showForRateCodes && !rules.showForRateCodes.includes(item.item.rate.rateCode)) {
        failedRules.push(`showForRateCodes: ruleValue: ${rules.showForRateCodes}, rateCodeInCartItem: ${item.item.rate.rateCode}`);
      }
      
      if (rules.hideForRateCodes && rules.hideForRateCodes.includes(item.item.rate.rateCode)) {
        failedRules.push(`hideForRateCodes: ruleValue: ${rules.hideForRateCodes}, rateCodeInCartItem: ${item.item.rate.rateCode}`);
      }

      if (rules.showIfBookingCodeIs && !rules.showIfBookingCodeIs.includes(item.searchParams.bookingCode)) {
        failedRules.push(`showIfBookingCodeIs: ruleValue: ${rules.showIfBookingCodeIs}, rateCodeInCartItem: ${item.searchParams.bookingCode}`);
      }

      if (rules.hideIfBookingCodeIs && rules.hideIfBookingCodeIs.includes(item.searchParams.bookingCode)) {
        failedRules.push(`hideIfBookingCodeIs: ruleValue: ${rules.hideIfBookingCodeIs}, rateCodeInCartItem: ${item.searchParams.bookingCode}`);
      }
      
      if (rules.showIfArrivalIsAfter && new Date(item.searchParams.arrival) <= new Date(rules.showIfArrivalIsAfter)) {
        failedRules.push(`showIfArrivalIsAfter: ruleValue: ${rules.showIfArrivalIsAfter}, arrivalInCartItem: ${item.searchParams.arrival}`);
      }
      
      if (rules.showIfArrivalIsBefore && new Date(item.searchParams.arrival) >= new Date(rules.showIfArrivalIsBefore)) {
        failedRules.push(`showIfArrivalIsBefore: ruleValue: ${rules.showIfArrivalIsBefore}, arrivalInCartItem: ${item.searchParams.arrival}`);
      }
      
      if (rules.showIfCategoryIs && !rules.showIfCategoryIs.includes(item.item.room.roomCategory)) {
        failedRules.push(`showIfCategoryIs: ruleValue: ${rules.showIfCategoryIs}, categoryInCartItem: ${item.item.room.roomCategory}`);
      }
      
      if (rules.showIfHotelCodeIs && !rules.showIfHotelCodeIs.includes(item.searchParams.hotelCode)) {
        failedRules.push(`showIfHotelCodeIs: ruleValue: ${rules.showIfHotelCodeIs}, hotelCodeInCartItem: ${item.searchParams.hotelCode}`);
      }

      results.push({hidden: failedRules.length > 0, reasons: failedRules});
    });

    let final = {
      hiddenInAllRoomTypes: results.every((item:any) => item.hidden === true),
      rooms: results
    }
    return final;
  }


}
